/**
 * Adobe primetime entry.
 */

// WordPress utilities.
import { __ } from '@wordpress/i18n';

// React component manager and React components.
import reactComponentManager from 'utils/reactComponentManager';
import AdobePrimetime from 'components-react/adobe-primetime';

// Global data store.
import { requestStore } from 'redux/share-store';
import withProvider from 'redux/with-provider';

// Adobe.
import sendEvent from 'adobe-primetime/sendEvent';
import {
  adobeUserAuthenticated,
  adobeUserAuthorized,
  adobeUserUnauthorized,
} from 'redux/modules/tve';

// Obtain global data store.
const store = requestStore();

/**
 * Global callback fired after identifying the requestor with Adobe.
 *
 * @see http://tve.helpdocsonline.com/error-reporting$advanced
 */
const receiveConfig = () => {
  // The 'errorEvent' function must be given as a string. Closures won't work.
  window.AE.bind('errorEvent', 'onAdobePrimetimeErrorEvent');
  window.AE.checkAuthentication();
};

/**
 * Global callback used for metrics tracking.
 *
 * @param eventType string Event type.
 * @param eventData object Event data.
 */
const receiveTrackingData = (eventType, eventData) => {
  const details = {
    eventType,
  };

  // This indicates only the user's selection of the MVPD that they want to authenticate with.
  if ('mvpdSelection' === eventType) {
    const [mvpd] = eventData;
    details.mvpd = mvpd;
  }

  if ('authenticationDetection' === eventType) {
    const [authenticated, mvpd] = eventData;
    details.authenticated = authenticated;
    details.mvpd = mvpd;

    if (authenticated) {
      store.dispatch(adobeUserAuthenticated(mvpd));

      /**
       * See NXS-3056. This string is hardcoded for this provider because it's
       * not currently clear whether Adobe will provide it as part of the error
       * details when a user is unauthorized. See NXS-3247. If the string is
       * not available programmatically, we may wish to make it possible to
       * override strings on a provider-specific basis in settings. In that
       * case, we may also wish to keep the merged combination of strings from
       * the server and defaults provided in JS in the Redux store.
       */
      if ('Comcast_SSO' === mvpd && window.adobePrimetime) {
        window.adobePrimetime.strings.lock_screen.unauthorized = __(
          // eslint-disable-next-line max-len
          'This network is not included in your current television subscription. Please contact your TV provider to subscribe.',
          'nexstar',
        );
      }
    }
  }

  if ('authorizationDetection' === eventType) {
    const [authorized, mvpd] = eventData;
    details.authorization = authorized;
    details.mvpd = mvpd;

    store.dispatch(
      authorized ? adobeUserAuthorized() : adobeUserUnauthorized(),
    );
  }

  sendEvent(details);
};

/**
 * Advanced Error Reporting global callback function.
 *
 * @param {{errorId: string, level: string, message: string}} errorData
 */
const receiveError = (errorData) => {
  const { errorId } = errorData;

  if ('Z100' === errorId) {
    // TODO: We need to know more specifically what errorData will contain.
  }
};

const init = () => {
  if (window.AccessEnabler) {
    window.sendTrackingData = receiveTrackingData;
    window.setConfig = receiveConfig;
    window.onAdobePrimetimeErrorEvent = receiveError;
  }

  reactComponentManager({
    AdobePrimetime: withProvider(AdobePrimetime),
  });
};

if ('1' === window.adobePrimetime.use_ovp) {
  window.addEventListener('OvpVideoEvent', (e) => {
    if ('VIDEO_INIT' === e?.detail?.name) {
      init();
    }
  });
} else {
  // Ensure the main Anvato global is set.
  window.anvp = window.anvp || {};
  window.anvp.onReady = () => {
    init();
  };

  if (module.hot) {
    module.hot.accept();
    // This is required for HMR.
    window.anvp.onReady = () => {
      init();
    };
  }
}
